import Phaser from "phaser";
import { VirtualJoystick as LibraryJoystick } from "phaser-virtual-joystick";
import { COLORS, DESIGN_HEIGHT, LAYOUT } from "../constants";
import { registerAutomationTarget } from "../automation/automationTags";

type VirtualJoystickOptions = {
  x: number;
  y: number;
  baseRadius?: number;
  thumbRadius?: number;
};

export type JoystickVector = {
  x: number; // -1 to 1
  y: number; // -1 to 1
};

/**
 * Wrapper around phaser-virtual-joystick library that maintains
 * compatibility with the existing API while providing Brawl Stars-inspired
 * smooth following behavior.
 *
 * Note: This is NOT a Phaser GameObject to avoid hit-test conflicts.
 * The library joystick manages itself as a Container.
 */
export class VirtualJoystick {
  private libraryJoystick: LibraryJoystick;
  private moveCallback?: (vector: JoystickVector) => void;
  private isActiveState = false;
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene, options: VirtualJoystickOptions) {
    this.scene = scene;

    const baseRadius = options.baseRadius ?? 64;
    const thumbRadius = options.thumbRadius ?? 40;
    const deadZoneRadius = 16;

    // Calculate bounds for the full screen
    // Use actual screen dimensions, not design dimensions
    const screenWidth = scene.scale.width;
    const screenHeight = scene.scale.height;
    const topPadding = screenHeight * 0.2;

    // Create the library joystick with Brawl Stars-inspired styling
    // Colors must be numbers (hex), not strings
    this.libraryJoystick = new LibraryJoystick({
      scene: scene,
      // Bounds use screen coordinates (full screen with top padding)
      bounds: {
        topLeft: {
          x: 0,
          y: topPadding,
        },
        bottomRight: {
          x: screenWidth,
          y: screenHeight,
        },
      },
      // Brawl Stars-inspired styling with game colors
      deadZone: {
        radius: deadZoneRadius,
        fillColor: COLORS.buttonFill,
        alpha: 0.3,
        strokeColor: COLORS.buttonStroke,
        strokeAlpha: 0.5,
        strokeWidth: 2,
      },
      baseArea: {
        radius: baseRadius,
        fillColor: COLORS.panelFill,
        alpha: 0.6,
        strokeColor: COLORS.buttonStroke,
        strokeAlpha: 0.8,
        strokeWidth: 3,
      },
      stick: {
        radius: thumbRadius,
        fillColor: COLORS.buttonFill,
        alpha: 0.9,
        strokeColor: COLORS.buttonStroke,
        strokeAlpha: 1.0,
        strokeWidth: 2,
      },
      // Enable on desktop for testing
      enableWithoutTouch: true,
    });

    // Add library joystick to scene (it manages itself)
    scene.add.existing(this.libraryJoystick);
    // Ensure it's on top and doesn't scroll with camera
    this.libraryJoystick.setScrollFactor(0);
    this.libraryJoystick.setDepth(1000);

    // Set up event listeners
    this.libraryJoystick.on("move", (data: { x: number; y: number }) => {
      this.isActiveState = true;
      const vector: JoystickVector = {
        x: Phaser.Math.Clamp(data.x, -1, 1),
        y: Phaser.Math.Clamp(data.y, -1, 1),
      };
      this.moveCallback?.(vector);
    });

    this.libraryJoystick.on("press", () => {
      this.isActiveState = true;
    });

    this.libraryJoystick.on("release", () => {
      this.isActiveState = false;
      this.moveCallback?.({ x: 0, y: 0 });
    });

    this.registerAutomation();
  }

  /**
   * Update the joystick (must be called in scene update loop for smooth following)
   */
  update() {
    this.libraryJoystick.update();
  }

  /**
   * Register callback for joystick movement
   */
  onMove(callback: (vector: JoystickVector) => void) {
    this.moveCallback = callback;
  }

  /**
   * Check if joystick is currently active (being dragged)
   */
  isActive(): boolean {
    return this.isActiveState;
  }

  private registerAutomation() {
    const cleanup = registerAutomationTarget(this.scene, {
      type: "button",
      name: "joystick",
      getBounds: () => {
        // Get bounds from the library joystick's base area
        const baseArea = this.libraryJoystick.baseArea;
        if (baseArea) {
          const bounds = baseArea.getBounds();
          return new Phaser.Geom.Rectangle(bounds.x, bounds.y, bounds.width, bounds.height);
        }
        // Fallback to a default bounds
        return new Phaser.Geom.Rectangle(
          LAYOUT.screenPadding,
          DESIGN_HEIGHT - LAYOUT.screenPadding - 128,
          128,
          128
        );
      },
      getText: () => "joystick",
    });

    // Clean up when library joystick is destroyed
    this.libraryJoystick.once(Phaser.GameObjects.Events.DESTROY, cleanup);
  }

  destroy() {
    // Clean up library joystick
    if (this.libraryJoystick) {
      // Note: The library uses scene.events, not standard event emitter
      // So we can't use off() - the library handles cleanup in its destroy()
      this.libraryJoystick.destroy();
    }
  }
}
